import { useState, useEffect, useCallback } from 'react';
import {
  LeaderboardUser,
  LeaderboardStats,
  UserStats,
  RecentTransaction,
  LeaderboardResponse,
  UseLeaderboardOptions,
  UseLeaderboardReturn,
  UseUserStatsReturn,
  UseRecentTransactionsReturn,
} from '../types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_LEADERBOARD_API_URL || 'http://localhost:3037/api/leaderboard';

// Generic API fetch function
async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`);
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  
  const data: LeaderboardResponse<T> = await response.json();
  
  if (!data.success) {
    throw new Error(data.error || 'API request failed');
  }
  
  return data.data;
}

// Main leaderboard hook
export function useLeaderboard(options: UseLeaderboardOptions = {}): UseLeaderboardReturn {
  const {
    filter = 'overall',
    limit = 100,
    autoRefresh = true,
    refreshInterval = 30000, // 30 seconds
  } = options;

  const [data, setData] = useState<LeaderboardUser[]>([]);
  const [stats, setStats] = useState<LeaderboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let endpoint = '';
      switch (filter) {
        case 'overall':
          endpoint = `/overall/${limit}`;
          break;
        case 'usdc':
          endpoint = `/token/USDC/${limit}`;
          break;
        case 'usdt':
          endpoint = `/token/USDT/${limit}`;
          break;
        case 'to_shido':
          endpoint = `/direction/to_shido/${limit}`;
          break;
        case 'from_shido':
          endpoint = `/direction/from_shido/${limit}`;
          break;
        default:
          endpoint = `/overall/${limit}`;
      }

      const [leaderboardData, statsData] = await Promise.all([
        fetchApi<LeaderboardUser[]>(endpoint),
        fetchApi<LeaderboardStats>('/stats'),
      ]);

      setData(leaderboardData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard data');
      console.error('Leaderboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, limit]);

  const refresh = useCallback(async () => {
    await fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Initial fetch
  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(fetchLeaderboard, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchLeaderboard]);

  return {
    data,
    stats,
    loading,
    error,
    refresh,
  };
}

// User stats hook
export function useUserStats(address: string | null): UseUserStatsReturn {
  const [data, setData] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserStats = useCallback(async () => {
    if (!address) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const userData = await fetchApi<UserStats>(`/user/${address}`);
      setData(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user stats');
      setData(null);
      console.error('User stats fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [address]);

  const refresh = useCallback(async () => {
    await fetchUserStats();
  }, [fetchUserStats]);

  useEffect(() => {
    fetchUserStats();
  }, [fetchUserStats]);

  return {
    data,
    loading,
    error,
    refresh,
  };
}

// Recent transactions hook
export function useRecentTransactions(limit: number = 50): UseRecentTransactionsReturn {
  const [data, setData] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecentTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const transactions = await fetchApi<RecentTransaction[]>(`/recent/${limit}`);
      setData(transactions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch recent transactions');
      console.error('Recent transactions fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const refresh = useCallback(async () => {
    await fetchRecentTransactions();
  }, [fetchRecentTransactions]);

  useEffect(() => {
    fetchRecentTransactions();
  }, [fetchRecentTransactions]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchRecentTransactions, 15000);
    return () => clearInterval(interval);
  }, [fetchRecentTransactions]);

  return {
    data,
    loading,
    error,
    refresh,
  };
}

// Health check hook
export function useHealthCheck() {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      setIsHealthy(response.ok);
      setLastCheck(new Date());
    } catch (err) {
      setIsHealthy(false);
      setLastCheck(new Date());
      console.error('Health check failed:', err);
    }
  }, []);

  useEffect(() => {
    checkHealth();
    
    // Check health every 60 seconds
    const interval = setInterval(checkHealth, 60000);
    return () => clearInterval(interval);
  }, [checkHealth]);

  return {
    isHealthy,
    lastCheck,
    checkHealth,
  };
}

// Utility hook for formatting addresses
export function useAddressFormatter() {
  const formatAddress = useCallback((address: string, length: number = 6): string => {
    if (!address) return '';
    if (address.length <= length * 2 + 2) return address;
    return `${address.slice(0, length + 2)}...${address.slice(-length)}`;
  }, []);

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      return false;
    }
  }, []);

  return {
    formatAddress,
    copyToClipboard,
  };
}

// Utility hook for number formatting
export function useNumberFormatter() {
  const formatVolume = useCallback((volume: string | number): string => {
    const num = typeof volume === 'string' ? parseFloat(volume) : volume;
    if (isNaN(num)) return '0';
    
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    } else {
      return num.toFixed(2);
    }
  }, []);

  const formatNumber = useCallback((num: number): string => {
    return new Intl.NumberFormat().format(num);
  }, []);

  const formatPercentage = useCallback((value: number, total: number): string => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  }, []);

  return {
    formatVolume,
    formatNumber,
    formatPercentage,
  };
}

// Utility hook for date formatting
export function useDateFormatter() {
  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  const formatDateTime = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatTimeAgo = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return formatDate(dateString);
  }, [formatDate]);

  return {
    formatDate,
    formatDateTime,
    formatTimeAgo,
  };
}
